import * as React from "react";
import ReactTooltip from 'react-tooltip';
import {cx} from 'emotion';

import "./customization-menu.scss";

import {cs}                   from "@Common/react/chain-services";
import {consumeContext}       from "@Common/react/context";
import {scopedState}          from "@Common/react/scoped-state";
import {UseState}             from "@Common/react/use-state";
import {SliderCheckbox}       from "@Common/ui-components/slider-checkbox/slider-checkbox";
import {setNestedValue}       from "@Common/utils/arr-path";
import {equalDeep, oMapToArr} from "@Common/utils/objects";

import {MenuLayout} from "../menu-layout/menu-layout";

export const CustomizationMenu = ({agent, readOnly, dataUpdated}) => cs(
    consumeContext("apis"),
    consumeContext("company"),
    ["customizationData", ({company}, next) => next(agent.value && parseAgentDisplayData({agent: agent.value, company}))],
    ["uploading", (_, next) => UseState({next})],
    ["customizationState", ({customizationData}, next) => agent.value && UseState({
        _key: "customization",
        initValue: defaultState({agent, customizationData}),
        next
    })],
    ({apis, uploading, customizationData, customizationState}, next) => MenuLayout({
        title: "Customization",
        button: readOnly?.canEdit && {
            onClick: async () => {
                uploading.onChange(true);
                const result = await apis.next.session.updateAgent({updateFields: customizationState.value.inputs});
                if (result?.errorMessage) {
                    alert(result.errorMessage);
                } else if (result?.errorInfo) {
                    const errors = JSON.parse(result.errorInfo);
                    errors.map((error) => {
                        scopedState(customizationState, ['errors', error.mapping]).onChange(error.message)
                    });
                } else {
                    dataUpdated.onChange(false);
                    await agent.reload();
                }
                uploading.onChange(false);
            },
            disabled: readOnly?.inactive || equalDeep(defaultState({agent, customizationData}), customizationState.value) || 
                      uploading.value || missingRequiredData({customizationState}),
        },
        next,
    }),
    ({customizationState, customizationData}) => (
        <div className="customization-information-menu-s8i">
            <ReactTooltip  id="customization-information-menu" place="bottom" delayShow={100}/>
            <div className="input-card">
                <div className="check-line">
                    <div className="toggle-label">Allow Feed to Update User</div>
                    {SliderCheckbox({
                        disabled: readOnly?.inactive,
                        value: scopedState(customizationState, ['inputs', 'allowFeedUpdate']).value,
                        onChange: () => {
                            scopedState(customizationState, ['errors', 'allowFeedUpdate']).onChange(false);
                            scopedState(customizationState, ['inputs', 'allowFeedUpdate']).onChange(
                                !scopedState(customizationState, ['inputs', 'allowFeedUpdate']).value
                            );
                            dataUpdated.onChange(
                                !equalDeep(defaultState({agent, customizationData}), 
                                    setNestedValue(customizationState.value, ['inputs', 'allowFeedUpdate'], !customizationState.value.inputs.allowFeedUpdate)
                                )
                            );
                        },
                        xdQaName: "Allow Feed Update " + scopedState(customizationState, ['inputs', 'allowFeedUpdate']).value,
                    })}
                </div>
                {customizationState.value.errors.allowFeedUpdate && (
                    <div className="errorMessage">{customizationState.value.errors.allowFeedUpdate}</div>
                )}
            </div>
            {customizationData.map((datum, index) => (
                <div className="input-card" key={index}>
                    <div className="line">
                        <div className="label" data-for="customization-information-menu" data-tip={datum.label}>{datum.label}</div>
                        <input {...{
                            readOnly: readOnly?.inactive || !readOnly?.canEdit,
                            value: scopedState(customizationState, ['inputs', datum.mapping]).value,
                            onChange: (e) => {
                                let value = e.target.value; 
                                scopedState(customizationState, ['errors', datum.mapping]).onChange(false);
                                scopedState(customizationState, ['inputs', datum.mapping]).onChange(value);
                                dataUpdated.onChange(
                                    !equalDeep(defaultState({agent, customizationData}), 
                                        setNestedValue(customizationState.value, ['inputs', datum.mapping], value)
                                    )
                                );
                            },
                            className: cx("value", {"read-only": readOnly?.inactive || !readOnly?.canEdit}),
                            maxLength: datum?.maxLength,
                            "xd-qa-type": "textbox",
                            "xd-qa-name": datum.label,
                        }}/>
                    </div>
                    {scopedState(customizationState, ['errors', datum.mapping]).value && (
                        <div className="errorMessage">{scopedState(customizationState, ['errors', datum.mapping]).value}</div>
                    )}
                </div>
            ))}
        </div>
    )
);

const defaultState = ({agent, customizationData}) => { 
    let inputs = {idAgent: agent.value.idAgent};
    let errors = {};
    customizationData.map((datum) => {
        inputs[datum.mapping] = datum.value;
        errors[datum.mapping] = false;
    });
    inputs['allowFeedUpdate'] = !!agent.value.allowFeedUpdate;
    errors['allowFeedUpdate'] = false;
    return {
        inputs,
        errors,
    }
};

const missingRequiredData = ({customizationState}) => {
    return !!scopedState(customizationState, ['errors', "agentDisplay1"]).value ||
    !!scopedState(customizationState, ['errors', "agentDisplay2"]).value ||
    !!scopedState(customizationState, ['errors', "agentDisplay3"]).value ||
    !!scopedState(customizationState, ['errors', "agentDisplay4"]).value ||
    !!scopedState(customizationState, ['errors', "agentDisplay5"]).value ||
    !!scopedState(customizationState, ['errors', "agentDisplay6"]).value ||
    !!scopedState(customizationState, ['errors', "agentDisplay7"]).value ||
    !!scopedState(customizationState, ['errors', "agentDisplay8"]).value 
}

export const parseAgentDisplayData = ({agent, company}) => {
    return oMapToArr(company.displayFields, (displayField, lineNumber) => {
        return {"label": displayField, "value": agent["agentDisplay" + lineNumber], "mapping": "agentDisplay" + lineNumber, "maxLength": parseInt(lineNumber) >= 7 ? 80 : 50 }
    });
};